import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { gridModels } from "@/mocks/models";
import DownloadLink from "@/components/DownloadLink";
import DownloadMotionLink from "@/components/DownloadMotionLink";

export default function ModelGrid() {
  const [favorites, setFavorites] = useState<Set<number>>(new Set());

  const toggleFavorite = (id: number) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const getBadgeStyle = (badge: string) => {
    let colors = {
      1: "bg-primary-500/90 text-white",
      2: "bg-accent-500/90 text-white",
      3: "bg-green-500/90 text-white",
      4: "bg-orange-500/90 text-white",
      5: "bg-yellow-500/90 text-background-50",
      6: "bg-blue-500/90 text-white",
      7: "bg-red-500/90 text-white"
    }
    switch (badge) {
      case "ライブ":
        return colors[1];
      case "VIP":
        return colors[2];
      case "NEW":
        return colors[3];
      case "HOT":
        return colors[4];
      case "TOP":
        return colors[5];
      case "近所":
        return colors[6];
      case "18+":
        return colors[7];
      default:
        return colors[Math.floor(Math.random() * 7) + 1];
        // return colors[1];
    }
  };

  return (
    <section className="px-4 py-4">
      <motion.div
        className="mb-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.25, 0.4, 0.25, 1] }}
      >
        <h1 className="mb-2 text-xl font-bold leading-tight text-foreground-50 md:text-3xl">
          {/* 今夜だけの出会い？ */}
          気になる女性と、今すぐ話そう。
          <br />
          <span className="text-primary-400 text-sm">
            {/* プライベートライブに今すぐ参加！ */}
            リアルタイムで配信中の女性をチェック。
            気になる相手と、ライブで気軽に会話できます
          </span>
        </h1>
        {/* <p className="text-sm leading-relaxed text-foreground-400 md:text-base">
          日本中のイケてる女性たちがライブ配信中。無修正のビデオチャットで、あなたの隠れた欲望を叶えてくれる。アプリをダウンロードして無料パスをGET。
        </p> */}
      </motion.div>
      <div className="mb-4 flex items-end justify-between">
        <h2 className="text-xs font-bold text-foreground-50 md:text-xl">
          {/* 近くでライブ中の女の子たち */}
          今オンラインの女性たち
          <span className="text-primary-400"> 💗</span>
        </h2>
        <DownloadLink
          downloadType="link"
          className="shrink-0 text-sm font-medium text-primary-400 transition-colors hover:text-primary-300"
        >
          すべて見る <i className="ri-arrow-right-s-line" />
        </DownloadLink>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
        {gridModels.map((model, index) => (
          <DownloadMotionLink
            key={model.id}
            downloadType="card"
            className="group relative overflow-hidden rounded-xl bg-background-100"
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{
              duration: 0.4,
              delay: index * 0.06,
              ease: [0.25, 0.4, 0.25, 1],
            }}
            whileHover={{ scale: 1.03, y: -2 }}
            whileTap={{ scale: 0.96 }}
          >
            <div className="relative aspect-[3/4] w-full overflow-hidden">
              <img
                src={model.image}
                alt={`${model.name} ${model.age}歳`}
                className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
              />
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

              {/* Badge */}
              <span
                className={`absolute left-2 top-2 rounded-full px-2 py-0.5 text-[10px] font-bold backdrop-blur-sm ${getBadgeStyle(model.badge)}`}
              >
                {
                  (() => {
                    if (model.badge === "ライブ") {
                      return (
                        <>
                          <i className="ri-fire-fill mr-0.5" />
                          ライブ
                        </>
                      )
                    } else if (model.badge === "18+") {
                      return "🔞 18+"
                    } else if (model.badge === "NEW") {
                      return (
                        <>
                          <i className="ri-sparkling-line mr-0.5" />
                          新人
                        </>
                      )
                    } else if (model.badge === "HOT") {
                      return (
                        <>
                          <i className="ri-fire-fill mr-0.5" />
                          激熱
                        </>
                      )
                    } else if (model.badge === "TOP") {
                      return (
                        <>
                          <i className="ri-vip-crown-fill mr-0.5" />
                          TOPアイドル
                        </>
                      )
                    }else if(model.badge === "近所"){
                      return (
                        <>
                          <i className="ri-map-pin-fill mr-0.5" />
                          近くの子
                        </>
                      )
                    }else{
                      return model.badge
                    }
                  })()
                }
              </span>

              {/* Heart button */}
              <motion.button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleFavorite(model.id);
                }}
                className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm transition-colors hover:bg-white/30"
                whileTap={{ scale: 0.8 }}
              >
                <AnimatePresence mode="wait">
                  <motion.i
                    key={favorites.has(model.id) ? "filled" : "line"}
                    className={
                      favorites.has(model.id)
                        ? "ri-heart-3-fill text-primary-400"
                        : "ri-heart-3-line"
                    }
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.5, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 500, damping: 25 }}
                  />
                </AnimatePresence>
              </motion.button>

              {/* Online dot */}
              {model.isLive && (
                <span className="absolute bottom-2 right-2 h-2.5 w-2.5 rounded-full border border-white/50 bg-green-400" />
              )}

              {/* Info */}
              <div className="absolute bottom-0 left-0 right-0 p-2.5">
                <div className="text-sm font-bold text-white">
                  {model.name}、{model.age}歳
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {model.tags.map((tag, i) => (
                    <span
                      key={i}
                      className="rounded bg-white/20 px-1.5 py-0.5 text-[10px] font-medium text-white/90 backdrop-blur-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </DownloadMotionLink>
        ))}
      </div>
    </section>
  );
}